import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Skeleton } from "@learn-me/ui";

interface CurrencyDistributionChartProps {
  data: Array<{
    currency: string;
    total: number;
    count: number;
    percentage: number;
  }>;
  isLoading?: boolean;
}

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
];

const CURRENCY_NAMES: Record<string, string> = {
  USD: "Dólares (USD)",
  CRC: "Colones (CRC)",
  EUR: "Euros (EUR)",
};

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function CurrencyDistributionChart({
  data,
  isLoading,
}: CurrencyDistributionChartProps) {
  if (isLoading) {
    return <Skeleton className="h-[250px] w-full" />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center text-muted-foreground">
        No hay datos de moneda para mostrar
      </div>
    );
  }

  const chartData = data.map((item) => ({
    ...item,
    name: CURRENCY_NAMES[item.currency] || item.currency,
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={80}
          fill="#8884d8"
          dataKey="total"
          label={({ payload }) => {
            const p = payload as { percentage: number };
            return `${p.percentage.toFixed(0)}%`;
          }}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload || payload.length === 0) return null;
            const data = payload[0].payload as {
              name: string;
              currency: string;
              total: number;
              count: number;
              percentage: number;
            };
            return (
              <div className="bg-popover border rounded-lg p-3 shadow-lg space-y-1">
                <div className="font-medium">{data.name}</div>
                <div className="text-sm">
                  Total: {formatCurrency(data.total, data.currency)}
                </div>
                <div className="text-sm">Ventas: {data.count}</div>
                <div className="text-sm">
                  Porcentaje: {data.percentage.toFixed(1)}%
                </div>
              </div>
            );
          }}
        />
        <Legend
          formatter={(value) => <span className="text-sm">{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
